import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellRing, Loader2 } from 'lucide-react';
import { getAuthHeaders } from '../utils/auth';
import { isNativeApp, nativePushPermissionState, registerNativePush } from '../utils/nativePush';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

export const PushToggle = () => {
  const [state, setState] = useState('checking'); // checking | unsupported | off | on | busy

  useEffect(() => {
    (async () => {
      if (isNativeApp()) {
        try {
          const perm = await nativePushPermissionState();
          setState(perm === 'granted' ? 'on' : 'off');
        } catch (e) { setState('off'); }
        return;
      }
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setState('unsupported'); return; }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setState(sub ? 'on' : 'off');
      } catch (e) { setState('off'); }
    })();
  }, []);

  const enable = async () => {
    setState('busy');
    if (isNativeApp()) {
      try {
        const res = await registerNativePush();
        if (res.denied) { toast.error('Notifications blocked — allow them in iPhone Settings → LEE'); setState('off'); return; }
        setState('on');
        toast.success('Lock-screen alerts enabled on this iPhone');
      } catch (e) {
        toast.error('Could not enable notifications: ' + (e?.message || 'unknown error'));
        setState('off');
      }
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { toast.error('Notifications blocked — allow them in Settings for LEE'); setState('off'); return; }
      const { data } = await axios.get(`${API}/push/vapid-public-key`, { headers: getAuthHeaders() });
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON() }, { headers: getAuthHeaders() });
      setState('on');
      toast.success('Lock-screen alerts enabled on this device');
    } catch (e) {
      toast.error('Could not enable notifications' + (e?.response?.status === 503 ? ' — push not configured on server' : ''));
      setState('off');
    }
  };

  const test = async () => {
    try {
      const { data } = await axios.post(`${API}/push/test`, {}, { headers: getAuthHeaders() });
      if (data.sent > 0) toast.success('Test notification sent — check your lock screen');
      else toast.error(`No device received it (sent ${data.sent}, failed ${data.failed || 0})`);
    } catch (e) { toast.error('Test push failed'); }
  };

  if (state === 'checking') return null;
  if (state === 'unsupported') {
    return (
      <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] px-3.5 py-2.5 text-[12px] text-zinc-500" data-testid="push-unsupported">
        This browser can't receive push. On iPhone: Share → Add to Home Screen, then open LEE from the icon to enable alerts.
      </div>
    );
  }
  if (state === 'on') {
    return (
      <div className="flex items-center justify-between gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.05] px-3.5 py-2.5" data-testid="push-on-banner">
        <span className="flex items-center gap-2 text-[12px] text-emerald-300 min-w-0">
          <BellRing className="w-3.5 h-3.5 shrink-0" /> Alerts on — task results hit your lock screen
        </span>
        <button onClick={test} className="text-[11px] font-bold text-zinc-400 hover:text-white transition-colors shrink-0" data-testid="push-test-btn">
          Send test
        </button>
      </div>
    );
  }
  return (
    <button onClick={enable} disabled={state === 'busy'}
      className="w-full flex items-center justify-between gap-2 rounded-xl border border-amber-500/25 bg-amber-500/[0.07] px-3.5 py-2.5 hover:bg-amber-500/[0.12] transition-colors disabled:opacity-60"
      data-testid="push-enable-btn">
      <span className="flex items-center gap-2 text-[12px] text-amber-200 font-semibold">
        {state === 'busy' ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" /> : <Bell className="w-3.5 h-3.5 shrink-0" />}
        {state === 'busy' ? 'Enabling…' : 'Enable notifications on this device'}
      </span>
      <span className="text-[11px] text-amber-400/70 shrink-0">tap once</span>
    </button>
  );
};
